'use client';

import Image from 'next/image';
import clsx from 'clsx';
import cn from '@/utils/classnames';
import { Link } from '@/i18n/navigation';
import useFn from '@/hooks/use-fn';
import { showLoginModal } from '@/features/auth/components/login-form';
import { CardFantasyItem, LineupStatus, MatchItemStatus } from '@/features/home/services/home';
import iconCricket from '@/features/home/images/fantasy/cricket.png';
import iconRound from '@/features/home/images/fantasy/icon-logo.png';
import iconLink from '@/features/home/images/fantasy/arrow.png';
import LineOutImg from '@/features/home/images/fantasy/linesup-normal.svg';
import IconMega from '@/features/home/images/fantasy/mega.png';
import styles from '../index.module.css';
import { ReactNode } from 'react';
import { menuTabUrl } from '@/const/page-url';

interface FantasyMatchCardProps {
  item: CardFantasyItem;
  index: number;
  lineOne: ReactNode;
  lineTwo: ReactNode;
  inViewport: boolean;
  isLogin: boolean;
  renderCurrency: (currencyList: Array<{ currency_type: string; price_pool: number; }>) => string;
  isLineupStatusOut: (lineupStatus: LineupStatus) => boolean;
  LineupStatusPopover: () => ReactNode;
  handleTourClick: (item: CardFantasyItem) => void;
  cardRef: React.RefObject<HTMLDivElement | null>;
  handleNavigate: () => void;
  t: (key: any) => string;
}

export default function FantasyMatchCard({
  item,
  index,
  lineOne,
  lineTwo,
  inViewport,
  isLogin,
  renderCurrency,
  isLineupStatusOut,
  LineupStatusPopover,
  handleTourClick,
  cardRef,
  handleNavigate: handleNavigateWithTracking,
  t,
}: FantasyMatchCardProps) {
  const handleNavigate = useFn((e: any) => {
    if (!isLogin) {
      showLoginModal({
        source: 'Fantasy',
      });
      e.preventDefault();
      return;
    }
    // 调用埋点处理函数
    handleNavigateWithTracking();
  });

  return (
    <div ref={cardRef} className={clsx(styles.fantasyCard, 'min-h-[130px]', index !== 0 && 'mt-[10px]')}>
      {/* 事件头部 */}
      <div className="flex items-center justify-between">
        <div className='flex items-center min-w-0 flex-1' onClick={(e) => {
          if (!isLogin) {
            showLoginModal({
              source: 'Fantasy',
            });
            return;
          }
          e.preventDefault();
          handleTourClick(item);
        }}>
          {inViewport && (
            <Image width={12} height={12} className='bg-white rounded-full mr-[5px] flex-shrink-0' src={iconCricket} alt={item?.full_name} />
          )}
          <div className='min-w-0 flex-1 flex items-center pr-8'>
            <h3 className="pr-[5px] overflow-hidden text-ellipsis whitespace-nowrap text-secondary text-[12px] font-medium">
              {item?.full_name}
            </h3>
            {inViewport && (
              <Image src={iconLink} alt={item?.full_name} width={5} className='flex-shrink-0' />
            )}
          </div>
        </div>
        <div className='flex items-center'>
          {
            isLineupStatusOut(item?.lineup_status) ?
              <>
                <LineOutImg
                  fill={'#3EDA5D'}
                ></LineOutImg>
                <span className="pl-[2px] text-[#3EDA5D] text-ss leading-[12px] font-medium">{t('lineupsOut')}</span>
              </>
              :
              <LineupStatusPopover />
          }
        </div>
      </div>

      {/* 比赛详情 */}
      <Link
        href={`${menuTabUrl.fantasy}?match_id=${item.id}`}
        onNavigate={handleNavigate}
      >
        <div className={clsx(styles.fantasyCardContent, 'flex items-center justify-between')}>
          <div className='flex-1 min-w-0'>
            {
              item?.squads.map((squad, squadIndex) => (
                <div key={squadIndex} className='w-full'>
                  {/* 队伍 */}
                  <div className={cn('flex items-center justify-between', squadIndex !== 0 && 'pt-[8px]')}>
                    <div className="flex items-center min-w-0 flex-1">
                      {inViewport && (
                        <>
                          {
                            squad?.logo_url ? (
                              <img className='rounded-full w-[22px] h-[22px] flex-shrink-0' src={squad?.logo_url} alt={squad?.full_name} loading="lazy" />
                            ) : (
                              <Image className='rounded-full w-[22px] h-[22px] flex-shrink-0' src={iconRound} alt={squad?.full_name} width={22} height={22} />
                            )
                          }
                        </>
                      )}
                      <div className="flex items-center pl-[7px] min-w-0 flex-1">
                        <div className="text-white font-semibold whitespace-nowrap text-[13px] pr-[10px] flex-shrink-0">{squad?.short_name}</div>
                        <div className={clsx('text-tertiary pr-1 text-ss font-medium overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1 !leading-[14px]')}>{squad?.full_name}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
          <div className={clsx(styles.line)}></div>
          <div className='flex-shrink-0 w-[86px] flex flex-col items-end'>
            <div className={clsx('whitespace-nowrap text-[12px] font-medium text-tertiary relative')}>
              {
                item.status === MatchItemStatus.MATCH_STATUS_IN_PROGRESS ?
                  <span className='text-accent-red text-ss font-medium'>{t('timesup')}</span> : lineOne
              }
            </div>
            {lineTwo && (
              <div
                className={clsx(
                  'pt-[7px] text-ss text-tertiary font-medium relative',
                )}
              >
                {lineTwo}
              </div>
            )}
          </div>
        </div>
      </Link>
      {/* isPrizeComingSoon */}
      {
        item?.is_prize_coming_soon ? (
          <div className="flex items-center">
            <Image src={IconMega} alt="time" width={14} height={14} />
            <div className="flex items-center text-accent-yellow text-xs font-semibold leading-[10px] pl-1">
              {t('comeingSoon')}
            </div>
          </div>
        ) : <>
          {
            (item?.price_pool_list?.length > 0 && item?.price_pool_list[0]?.price_pool > 0) && (
              <div className="flex items-center text-accent-yellow text-xs font-semibold leading-[10px]">
                {inViewport && (
                  <Image src={IconMega} alt="mega" width={14} height={14} />
                )}
                <span className='pl-[4px]'>
                  {renderCurrency(item?.price_pool_list)}
                </span>
              </div>
            )
          }
        </>
      }
    </div>
  );
}

